import React from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  PieChart,
  LineChart,
  BrainCircuit,
  Database,
  LayoutDashboard,
  GitBranch,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Service = {
  title: string;
  description: string;
  icon: LucideIcon;
  gradient: string;
};

const services: Service[] = [
  {
    title: "Business Intelligence",
    description: "Interactive dashboards and reports that give leaders real-time visibility into KPIs.",
    icon: LayoutDashboard,
    gradient: "da-service-card__icon--blue",
  },
  {
    title: "Data Visualization",
    description: "Clear, compelling charts and visuals that make complex data easy to understand.",
    icon: PieChart,
    gradient: "da-service-card__icon--cyan",
  },
  {
    title: "Predictive Analytics",
    description: "Forecast trends, demand, and outcomes using machine learning and statistical models.",
    icon: LineChart,
    gradient: "da-service-card__icon--teal",
  },
  {
    title: "AI & Machine Learning",
    description: "Build custom ML models for classification, recommendation, and anomaly detection.",
    icon: BrainCircuit,
    gradient: "da-service-card__icon--purple",
  },
  {
    title: "Data Warehousing",
    description: "Centralize and organize data from multiple sources into a scalable warehouse.",
    icon: Database,
    gradient: "da-service-card__icon--indigo",
  },
  {
    title: "ETL & Data Pipeline",
    description: "Automate data extraction, transformation, and loading for clean, reliable datasets.",
    icon: GitBranch,
    gradient: "da-service-card__icon--violet",
  },
  {
    title: "Performance Analytics",
    description: "Track marketing, sales, and operational metrics to optimize business performance.",
    icon: BarChart3,
    gradient: "da-service-card__icon--green",
  },
  {
    title: "Data Governance",
    description: "Ensure data quality, security, compliance, and consistent access across teams.",
    icon: ShieldCheck,
    gradient: "da-service-card__icon--sky",
  },
];

const DaServices: React.FC = () => (
  <AnimatedSection as="section" className="da-section da-section--light" aria-labelledby="da-services-title">
    <div className="da-container">
      <SectionHeader
        eyebrow="Our Analytics Services"
        title="End-to-End Data Analytics Solutions"
        text="From raw data to strategic insights — we cover every layer of the analytics stack."
        light
      />

      <div className="da-service-grid">
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <motion.article
              key={service.title}
              className="da-service-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              whileHover={{ y: -8, transition: { duration: 0.25 } }}
            >
              <div className={`da-service-card__icon ${service.gradient}`}>
                <Icon size={24} aria-hidden="true" />
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </motion.article>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default DaServices;
